'use client'

import { useLocale } from '@/lib/i18n/use-locale'
import { legalContent } from '@/lib/i18n/legal'

interface PrivacyContentProps {
  lastUpdated: string
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')

export function PrivacyContent({ lastUpdated }: PrivacyContentProps) {
  const { locale } = useLocale()
  const content = legalContent[locale].privacy

  return (
    <main className="mx-auto max-w-6xl px-4 pb-20 pt-12">
      <section className="rounded-3xl border border-emerald-100/70 bg-white/90 p-10 shadow-xl shadow-emerald-100/40 backdrop-blur dark:border-emerald-900/40 dark:bg-gray-900/80 dark:shadow-none">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-300">
          {content.lastUpdatedLabel}: {lastUpdated}
        </p>
        <h1 className="mt-4 text-4xl font-semibold text-gray-900 dark:text-white sm:text-5xl">
          {content.title}
        </h1>
      </section>

      <div className="mt-10 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="sticky top-6 hidden h-fit rounded-2xl border border-gray-200/70 bg-white p-6 text-sm text-gray-600 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 lg:block">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Sections</p>
          <ul className="mt-4 space-y-3">
            {content.sections.map((section) => (
              <li key={section.title}>
                <a
                  href={`#${slugify(section.title)}`}
                  className="block rounded-lg px-3 py-2 transition hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <div className="space-y-10">
          {content.sections.map((section) => (
            <section key={section.title} id={slugify(section.title)} className="scroll-mt-24">
              <div className="rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
                <div className="mt-4 space-y-4 text-gray-600 dark:text-gray-300">
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.bullets && (
                    <ul className="list-disc space-y-2 pl-5">
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
